import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { userActionSet } from "../../redux/actions/userActions";
import { partnerStorage } from "../../utils/storage/partnerStorage";

const PROFILE_PREFS_KEY = "preferences";

// STOPGAP: pinned workspace tabs are persisted to partner-scoped localStorage
// (keyed per user) instead of the user profile, because the update-profile
// endpoint isn't wired up yet. localStorage is the source of truth; we mirror
// it into the Redux user object so every consumer (WorkspaceTabs bar + pin
// buttons) re-renders. When the profile endpoint lands, restore the
// getLoggedInUser/updateUserById flow and drop the localStorage calls — the
// hook's public API does not change.
const STORAGE_KEY_BASE = "workspacePinnedTabs";
const storageKeyForUser = (userId) => `${STORAGE_KEY_BASE}:${userId}`;

const readStoredTabs = (userId) => {
  if (!userId) return [];
  try {
    const raw = partnerStorage.getItem(storageKeyForUser(userId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.error("Failed to read pinned tabs from storage:", err);
    return [];
  }
};

const writeStoredTabs = (userId, tabs) => {
  if (!userId) return;
  try {
    partnerStorage.setItem(storageKeyForUser(userId), JSON.stringify(tabs));
  } catch (err) {
    console.error("Failed to write pinned tabs to storage:", err);
  }
};

export const useWorkspaceTabs = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state?.user);
  const [loading] = useState(false);

  // The signed-in user comes from /auth/me (mirrored into Redux by
  // AuthProvider), whose id field varies (`_id` / `user_id` / `id`). Fall back
  // to the email so the storage key is always stable.
  const userKey =
    user?._id || user?.user_id || user?.id || user?.emailAddress || user?.email;

  const tabs = useMemo(() => {
    const stored = user?.[PROFILE_PREFS_KEY]?.workspacePinnedTabs;
    return Array.isArray(stored) ? stored : [];
  }, [user]);

  // Hydrate the Redux user prefs from localStorage once the user is known, so
  // pinned tabs survive a reload even though they never hit the backend.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!userKey || hydratedRef.current) return;
    hydratedRef.current = true;
    const storedTabs = readStoredTabs(userKey);
    const currentTabs = user?.[PROFILE_PREFS_KEY]?.workspacePinnedTabs;
    if (!Array.isArray(currentTabs) || currentTabs.length === 0) {
      if (storedTabs.length === 0) return;
      dispatch(
        userActionSet({
          ...user,
          [PROFILE_PREFS_KEY]: {
            ...(user[PROFILE_PREFS_KEY] || {}),
            workspacePinnedTabs: storedTabs,
          },
        }),
      );
    }
  }, [user, userKey, dispatch]);

  const mutateTabs = useCallback(
    (mutationFn) => {
      if (!userKey) return;

      // Read the freshest value from storage (the source of truth) so
      // concurrent pin/unpin actions don't clobber each other.
      const currentTabs = readStoredTabs(userKey);
      const updatedTabs = mutationFn(currentTabs);

      writeStoredTabs(userKey, updatedTabs);
      dispatch(
        userActionSet({
          ...user,
          [PROFILE_PREFS_KEY]: {
            ...(user[PROFILE_PREFS_KEY] || {}),
            workspacePinnedTabs: updatedTabs,
          },
        }),
      );
    },
    [user, userKey, dispatch],
  );

  // `pipeline` records which drilldown the loan belongs to ("underwriting" vs
  // "acquisitions") so the tab links to the right base route (/loan vs
  // /acquisition). Tabs pinned before this existed have no `pipeline` and are
  // treated as underwriting by consumers.
  const addTab = useCallback(
    ({ _id, loanNumber, loanName, pipeline }) => {
      mutateTabs((currentTabs) => {
        if (currentTabs.some((t) => t._id === _id)) return currentTabs;
        return [...currentTabs, { _id, loanNumber, loanName, pipeline }];
      });
    },
    [mutateTabs],
  );

  const removeTab = useCallback(
    (loanNumber) => {
      mutateTabs((currentTabs) =>
        currentTabs.filter((t) => String(t.loanNumber) !== String(loanNumber)),
      );
    },
    [mutateTabs],
  );

  const renameTab = useCallback(
    (loanNumber, nextName) => {
      const trimmed = (nextName || "").trim();
      mutateTabs((currentTabs) =>
        currentTabs.map((t) =>
          String(t.loanNumber) === String(loanNumber)
            ? { ...t, loanName: trimmed }
            : t,
        ),
      );
    },
    [mutateTabs],
  );

  // Drag-reorder: move the dragged tab to the position of the tab it was
  // dropped on. Keyed by `_id` rather than index so a stale render index (or a
  // concurrent pin/unpin) can't move the wrong tab.
  const reorderTab = useCallback(
    (fromId, toId) => {
      if (!fromId || !toId || String(fromId) === String(toId)) return;
      mutateTabs((currentTabs) => {
        const from = currentTabs.findIndex(
          (t) => String(t._id) === String(fromId),
        );
        const to = currentTabs.findIndex((t) => String(t._id) === String(toId));
        if (from === -1 || to === -1 || from === to) return currentTabs;
        const next = currentTabs.slice();
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        return next;
      });
    },
    [mutateTabs],
  );

  const isTabPinned = useCallback(
    (loanNumber) => {
      return tabs.some((t) => String(t.loanNumber) === String(loanNumber));
    },
    [tabs],
  );

  const toggleTab = useCallback(
    ({ _id, loanNumber, loanName, pipeline }) => {
      if (isTabPinned(loanNumber)) {
        removeTab(loanNumber);
      } else {
        addTab({ _id, loanNumber, loanName, pipeline });
      }
    },
    [isTabPinned, addTab, removeTab],
  );

  return {
    tabs,
    loading,
    addTab,
    removeTab,
    renameTab,
    reorderTab,
    toggleTab,
    isTabPinned,
  };
};
